import { createFileRoute } from '@tanstack/react-router'
import { Container, Paper, Text, Stack, Image, Box, Grid, Group, Center } from '@mantine/core'
import { ResponsiveTitle } from '../../components/ResponsiveTitle/ResponsiveTitle'
import { InviteContent } from '@/components/InviteContent/InviteContent'
import { useSiteContent } from '@/hooks/useSiteContent'
import styles from './logged_in.$pin.rsvp.module.css'

export const Route = createFileRoute('/guest/logged_in/$pin/invitation')({
  component: GuestInvitation,
})
function InvitationDivider() {
  return (
    <Center>
      <Box w="min(100%, 600px)" px="xl" py="4rem">
        <Image
          src="/assets/divider.png"
          height={'auto'}
          opacity={0.6}
          width="100%"
          fit="contain"
          className="gold-tone"
        />
      </Box>
    </Center>
  )
}
function GuestInvitation() {
  const { content } = useSiteContent()

  const renderFlourish = (left: boolean) => {
    return (
      <Image
        src="/assets/flourish1.png"
        alt="mittelalterliche Verzierung"
        height={'150px'}
        fit="contain"
        radius="sm"
        flex={1}
        className={`${left ? styles.flourish1 : styles.flourish2} gold-tone`}
        mt="-100px"
        visibleFrom="md"
        opacity={0.8}
      />
    )
  }

  return (
    <Container size="lg" py="xl">
      <Paper className={styles.paper} shadow="xl" p="xl" bg="white">
        <Stack gap="xl">
          {/* Header Section - Full Width */}
          <Stack gap="xl">
            <Image src="/assets/burg.webp" alt="Burg" width={100} height={250} radius="sm" />
            <ResponsiveTitle
              size="4rem"
              mobileSize="3rem"
              order={1}
              ta="center"
              className={styles.coupleNames}
              mt="xl"
            >
              {content?.event.coupleNames || ''}
            </ResponsiveTitle>

            {/* Date */}
            <Text ta="center" size="lg" fw={600} c="golden">
              am {content?.event.eventDate || ''}
            </Text>
          </Stack>

          {/* Einladung (Invitation) - Full Width */}
          <Group wrap="nowrap" mt="xl">
            {renderFlourish(true)}
            <Stack gap="md" flex={'auto'}>
              <Text ta="center" size="xl" className={`${styles.invitationText}`}>
                {content?.event.invitationText || 'laden herzlich ein zur'}
                <br />
                <strong>{content?.event.eventTitle || ''}</strong>
              </Text>
            </Stack>
            {renderFlourish(false)}
          </Group>

          <InvitationDivider />

          {/* Über die Hochzeit & Zeitplan - Two Columns */}
          <Grid gutter="xl" className={styles.contentGrid}>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="md">
                <Text ta="center" size="lg" fw={600} c="#000000">
                  Über die Hochzeit
                </Text>
                <InviteContent />
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="md">
                <Text ta="center" size="lg" fw={600} c="#000000">
                  Zeitplan
                </Text>
                <Stack gap="xs">
                  {content?.timeline.map((entry, index) => (
                    <Text key={index} size="sm" ta="center">
                      <strong>{entry.time}</strong> - {entry.description}
                    </Text>
                  ))}
                </Stack>
              </Stack>
            </Grid.Col>
          </Grid>

          <InvitationDivider />

          {/* Ort + Anfahrt - Combined Section */}
          <Stack gap="md">
            <Text ta="center" size="lg" fw={600} c="#000000">
              Ort & Anfahrt
            </Text>
            <Grid gutter="md" className={styles.locationGrid}>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack gap="xs" className={styles.locationInfo}>
                  <Text size="md" fw={600} c="#000000" ta="center">
                    Adresse
                  </Text>
                  <Text size="md" ta="center">
                    {content?.location.name || ''}
                    <br />
                    {content?.location.addressLine1 || ''}
                    <br />
                    {content?.location.addressLine2 || ''}
                  </Text>
                  <Box mt="xs">
                    <Image
                      visibleFrom="md"
                      mr="xs"
                      src="/assets/riders.png"
                      alt="Mittelalterliche Reiter"
                      opacity={0.8}
                      width={'auto'}
                    />
                  </Box>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="md">
                  <Box className={styles.mapContainer}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2567.105288252297!2d6.7857001!3d49.9531274!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bfd248b4641eb1%3A0x537fdc196be48741!2sBurg%20Bruch!5e0!3m2!1sen!2sde!4v1762525221341!5m2!1sen!2sde"
                      width="100%"
                      height="300"
                      style={{ border: 0, borderRadius: 8 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Hochzeitslocation auf Google Maps"
                    ></iframe>
                  </Box>
                  <Text ta="center">
                    <a
                      href="https://maps.app.goo.gl/AFWiRUAixsTfvSaC8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.mapLink}
                    >
                      In Google Maps öffnen →
                    </a>
                  </Text>
                </Stack>
              </Grid.Col>
            </Grid>
          </Stack>

          <InvitationDivider />

          {/* Gift Section - Full Width with Chest Background */}
          <Box className={styles.giftSection}>
            <Grid gutter="xl" align="center">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="md" className={styles.giftContent}>
                  <Text ta="center" size="lg" fw={600} c="#000000" className={styles.giftTitle}>
                    Geschenke
                  </Text>
                  <Text
                    ta="center"
                    size="md"
                    className={`${styles.giftText} ${styles.initialCapContainer}`}
                  >
                    {content?.invitation.giftText || ''}
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 0, md: 6 }}></Grid.Col>
            </Grid>
          </Box>

          <InvitationDivider />

          {/* Contact Information - Full Width */}
          <Center py="xl">
            <Stack gap="md" align="center">
              <Group wrap="nowrap" gap="lg" py="xl">
                <Image
                  src="/assets/qmark.png"
                  alt="Fragezeichen"
                  height={150}
                  fit="contain"
                  radius="sm"
                  mx="auto"
                  opacity={0.9}
                  flex={1}
                  className="gold-tone"
                />
                <Stack gap="sm" flex={2} align="center">
                  <Text ta="center" size="lg" fw={600} c="#000000">
                    Bei Fragen oder Sonderwünschen kontaktiert uns gerne direkt per WhatsApp oder
                    Telefon.
                  </Text>
                  <Stack gap="xs" mt="md">
                    <Text ta="center" size="md">
                      <strong>{content?.contact.primaryPhoneLabel || ''}:</strong>{' '}
                      <a
                        href={`tel:${content?.contact.primaryPhoneNumber || ''}`}
                        className={styles.mapLink}
                      >
                        {content?.contact.primaryPhoneNumber || ''}
                      </a>
                    </Text>
                    <Text ta="center" size="md">
                      <strong>{content?.contact.secondaryPhoneLabel || ''}:</strong>{' '}
                      <a
                        href={`tel:${content?.contact.secondaryPhoneNumber || ''}`}
                        className={styles.mapLink}
                      >
                        {content?.contact.secondaryPhoneNumber || ''}
                      </a>
                    </Text>
                  </Stack>
                </Stack>
                <Image
                  src="/assets/qmark.png"
                  alt="Fragezeichen"
                  height={150}
                  fit="contain"
                  radius="sm"
                  mx="auto"
                  opacity={0.9}
                  flex={1}
                  visibleFrom="md"
                  className="gold-tone"
                />
              </Group>
            </Stack>
          </Center>
        </Stack>
      </Paper>
    </Container>
  )
}
